import { type FC } from 'react';
import { useUnit } from 'effector-react';
import { useForm } from '@mantine/form';
import { Button, Group, NumberInput, Select, Stack, Text, TextInput } from '@mantine/core';
import { PayinBank, PayinPaymentMethod, useCreatePayinOrder } from '~/entities/payin';
import { $payinTraderInfo } from '~/entities/payin';
import { CardInput } from '~/shared/ui/CardInput';
import { PhoneInput } from '~/shared/ui/PhoneInput';
import { BindingAlert } from './components';
import { validateForm, buildCreateOrderPayload } from './lib';
import { bankOptions, type CreateOrderFormValues, type PaymentMethod } from './model';

export interface CreateOrderProps {
    paymentMethod: PaymentMethod;
    onCancel?: VoidFunction;
    onSuccess?: VoidFunction;
    onError?: (error: Error) => void;
}

export const CreateOrder: FC<CreateOrderProps> = ({ paymentMethod, onCancel, onSuccess, onError }) => {
    const { traderInfo } = useUnit({
        traderInfo: $payinTraderInfo,
    });

    const { mutate, isPending } = useCreatePayinOrder({
        onSuccess,
        onError,
    });

    const { paymentChannels = [], allowedMarkets = [] } = traderInfo?.config ?? {};
    const isOrderBindingConfigured = allowedMarkets.length > 0;

    const form = useForm<CreateOrderFormValues>({
        initialValues: {
            amount: {
                value: null,
            },
            requisite: {
                sbp: {
                    phoneNumber: '',
                    bank: PayinBank.BANK_UNSPECIFIED,
                },
                card: {
                    holder: '',
                    number: '',
                    bank: PayinBank.BANK_UNSPECIFIED,
                },
            },
            transactionId: '',
        },
        validateInputOnChange: true,
        validate: (values) => {
            return validateForm(values, paymentChannels, paymentMethod);
        },
    });

    const isSBP = paymentMethod === PayinPaymentMethod.METHOD_SBP;
    const isCard = paymentMethod === PayinPaymentMethod.METHOD_CARD;

    const handleSubmit = form.onSubmit((values) => {
        if (isOrderBindingConfigured) {
            return;
        }

        const payload = buildCreateOrderPayload(values, paymentChannels, paymentMethod);

        mutate(payload);
    });

    return (
        <form onSubmit={handleSubmit}>
            <Stack gap={16}>
                <Text
                    size='sm'
                    c='grey'
                >
                    Укажите сумму и заполните реквизиты.{' '}
                    {isOrderBindingConfigured ? 'Затем свяжите ордер с активным ордером на бирже.' : ''}
                </Text>

                <Stack gap={4}>
                    <NumberInput
                        size='xs'
                        label='Сумма'
                        placeholder='Введите сумму'
                        disabled={isPending}
                        {...form.getInputProps('amount.value')}
                    />

                    {isSBP && (
                        <Stack gap={4}>
                            <PhoneInput
                                size='xs'
                                label='Телефон (SBP)'
                                disabled={isPending}
                                {...form.getInputProps('requisite.sbp.phoneNumber')}
                            />

                            <Select
                                size='xs'
                                searchable
                                label='Банк'
                                placeholder='Выберите банк'
                                data={bankOptions}
                                disabled={isPending}
                                {...form.getInputProps('requisite.sbp.bank')}
                            />
                        </Stack>
                    )}

                    {isCard && (
                        <Stack gap={4}>
                            <TextInput
                                size='xs'
                                label='Владелец карты'
                                placeholder='IVAN IVANOV'
                                disabled={isPending}
                                {...form.getInputProps('requisite.card.holder')}
                            />

                            <CardInput
                                size='xs'
                                label='Номер карты'
                                disabled={isPending}
                                {...form.getInputProps('requisite.card.number')}
                            />
                        </Stack>
                    )}
                </Stack>

                {isOrderBindingConfigured && <BindingAlert />}

                <Group justify='flex-end'>
                    <Button
                        title='Создать ордер'
                        disabled={isPending || isOrderBindingConfigured}
                        loading={isPending}
                        type='submit'
                    >
                        Создать ордер
                    </Button>

                    {onCancel && (
                        <Button
                            title='Отмена'
                            color='red'
                            disabled={isPending}
                            onClick={onCancel}
                        >
                            Отмена
                        </Button>
                    )}
                </Group>
            </Stack>
        </form>
    );
};
